import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/services.actions';
import { servicesSelector } from 'redux/selectors/services.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import ServicesView from 'views/ServicesView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IService } from 'types/services.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/ServicesView/i18n';

const ServicesContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IService | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.services);
  const items = useTypedSelector(servicesSelector);

  const actions = [
    {
      label: i18next.t('SERVICES_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/services/${id}/edit`),
    },
    {
      label: i18next.t('SERVICES_VIEW:DELETE'),
      onClick: (id: string) =>
        setDeleteItem(items.find((item: IService) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'name',
      label: i18next.t('SERVICES_VIEW:NAME'),
    },
    {
      name: 'id',
      label: i18next.t('SERVICES_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('SERVICES_VIEW:TITLE')}>
      <ServicesView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/services/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t('SERVICES_VIEW:DELETE_DIALOG_MESSAGE', {
          name: `${deleteItem?.name}`,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t('SERVICES_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default ServicesContainer;
